import { call, put, takeLatest, all, delay } from 'redux-saga/effects';
import axios from 'axios';
import { toast } from 'react-toastify';

import {
  fetchProductListRequest,
  fetchProductListSuccess,
  fetchProductListFailure,
  fetchSingleProductRequest,
  fetchSingleProductSuccess,
  fetchSingleProductFailure,
  bookNowStatusChangeRequest,
  bookNowStatusChange,
  bookNowStatusChangeFailure,
  deleteProductRequest,
  deleteProductSuccess,
  deleteProductFailure,
  clearLoading,
  addProductRequest,
  addProductSuccess,
  addProductFailure,
  editProductRequest,
  editProductSuccess,
  editProductFailure,
} from './ProductListSlice';

// --- API Calls ---
const fetchProductListAPI = async (payload) => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/product/getproducts`,
    payload
  );
  const body = response?.data?.data;
  return {
    productlist: body?.productlist ?? [],
    producttermsandconditions: body?.producttermsandconditions ?? null,
  };
};

const fetchSingleProductAPI = async (id) => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/product/details/${id}`
  );
  return response.data.body;
};

const deleteProductAPI = async (id) => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/product/deleteProduct/${id}`
  );
  return response.data;
};

const addProductAPI = async (payload) => {
  const response = await axios.post(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/product/add`,
    payload
  );
  return response.data;
};

const editProductAPI = async ({ id, data }) => {
  const response = await axios.put(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/product/edit/${id}`,
    data
  );
  return response.data;
};

// --- Saga Workers ---
function* handleFetchProductList(action) {
  try {
    const response = yield call(fetchProductListAPI, action.payload);
    yield put(
      fetchProductListSuccess(
        response ?? { productlist: [], producttermsandconditions: null },
      ),
    );
  } catch (error) {
    yield put(fetchProductListFailure(error.message || 'Failed to fetch product list'));
  }
}

function* handleFetchSingleProduct(action) {
  try {
    const response = yield call(fetchSingleProductAPI, action.payload);
    yield put(fetchSingleProductSuccess(response));
  } catch (error) {
    yield put(fetchSingleProductFailure(error.message || 'Failed to fetch product'));
  }
}

function* toggleProductStatusSaga(action) {
  try {
    const { productId, enabled } = action.payload;
    yield call(axios.post, `${process.env.REACT_APP_API_BASE_URL}/api/v1/product/productBookStatusChange`, {
      id: productId.productId,
    });

    yield put(bookNowStatusChange({ productId, enabled }));
    yield delay(300);

    if (action.payload.refetchId) {
      yield put(fetchSingleProductRequest(action.payload.refetchId));
    }

    yield put(clearLoading());
    // yield call(toast.success, `Product has been ${enabled ? 'enabled' : 'disabled'} successfully.`);
  } catch (error) {
    yield put(bookNowStatusChangeFailure(error.message || 'Toggle failed'));
    yield call(toast.error, error.message || 'Something went wrong while toggling status.');
  }
}

function* handleDeleteProduct(action) {
  try {
    const { id } = action.payload;
    yield call(deleteProductAPI, id);
    yield put(deleteProductSuccess(id));
    yield call(toast.success, 'Product deleted successfully');
  } catch (error) {
    yield put(deleteProductFailure(error.message || 'Failed to delete product'));
    yield call(toast.error, error.message || 'Something went wrong while deleting.');
  }
}

function* handleAddProduct(action) {
  try {
    const response = yield call(addProductAPI, action.payload);
    yield put(addProductSuccess(response));
    yield call(toast.success, 'Product added successfully');
  } catch (error) {
    yield put(addProductFailure(error.message || 'Failed to add product'));
    yield call(toast.error, error.message || 'Something went wrong while adding product');
  }
}

function* handleEditProduct(action) {
  try {
    const response = yield call(editProductAPI, action.payload);
    yield put(editProductSuccess(response));
    yield call(toast.success, 'Product updated successfully');

    if (action.payload.id) {
      yield put(fetchSingleProductRequest(action.payload.id));
    }
  } catch (error) {
    yield put(editProductFailure(error.message || 'Failed to update product'));
    yield call(toast.error, error.message || 'Something went wrong while updating product');
  }
}

// --- Root saga ---
export default function* productListSaga() {
  yield all([
    takeLatest(fetchProductListRequest.type, handleFetchProductList),
    takeLatest(fetchSingleProductRequest.type, handleFetchSingleProduct),
    takeLatest(bookNowStatusChangeRequest.type, toggleProductStatusSaga),
    takeLatest(deleteProductRequest.type, handleDeleteProduct),
    takeLatest(addProductRequest.type, handleAddProduct),
    takeLatest(editProductRequest.type, handleEditProduct),
  ]);
}
